(() => {
  const months = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
  const sharedStorageKey = 'ls-fit-finder-session-shared-v1';
  const locationStorageKey = 'ls-soil-location-v1';

  const normalize = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const parseJson = (value, fallback = {}) => {
    try {
      return JSON.parse(value || '') || fallback;
    } catch (error) {
      return fallback;
    }
  };

  const normalizeZip = (value) => String(value || '').replace(/\D/g, '').slice(0, 5);

  const monthNumber = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return 0;
    if (/^\d+$/.test(raw)) {
      const number = Number(raw);
      return number >= 1 && number <= 12 ? number : 0;
    }
    const direct = months.indexOf(raw);
    if (direct >= 0) return direct + 1;
    const short = raw.slice(0, 3);
    const shortIndex = months.findIndex((month) => month.slice(0, 3) === short);
    return shortIndex >= 0 ? shortIndex + 1 : 0;
  };

  const monthName = (number) => months[number - 1]
    ? months[number - 1][0].toUpperCase() + months[number - 1].slice(1)
    : '';

  const states = [
    ['Alabama','AL'],['Alaska','AK'],['Arizona','AZ'],['Arkansas','AR'],['California','CA'],['Colorado','CO'],['Connecticut','CT'],['Delaware','DE'],['District of Columbia','DC'],['Florida','FL'],['Georgia','GA'],['Hawaii','HI'],['Idaho','ID'],['Illinois','IL'],['Indiana','IN'],['Iowa','IA'],['Kansas','KS'],['Kentucky','KY'],['Louisiana','LA'],['Maine','ME'],['Maryland','MD'],['Massachusetts','MA'],['Michigan','MI'],['Minnesota','MN'],['Mississippi','MS'],['Missouri','MO'],['Montana','MT'],['Nebraska','NE'],['Nevada','NV'],['New Hampshire','NH'],['New Jersey','NJ'],['New Mexico','NM'],['New York','NY'],['North Carolina','NC'],['North Dakota','ND'],['Ohio','OH'],['Oklahoma','OK'],['Oregon','OR'],['Pennsylvania','PA'],['Rhode Island','RI'],['South Carolina','SC'],['South Dakota','SD'],['Tennessee','TN'],['Texas','TX'],['Utah','UT'],['Vermont','VT'],['Virginia','VA'],['Washington','WA'],['West Virginia','WV'],['Wisconsin','WI'],['Wyoming','WY']
  ];

  const findState = (value) => {
    const raw = String(value || '').trim();
    const code = raw.split('-').pop().toUpperCase();
    return states.find(([name, stateCode]) => stateCode === code || name.toLowerCase() === raw.toLowerCase()) || null;
  };

  const readStorage = (key) => {
    try {
      return parseJson(window.localStorage.getItem(key), {});
    } catch (error) {
      return {};
    }
  };

  const writeSharedProfile = (patch) => {
    try {
      const current = readStorage(sharedStorageKey);
      window.localStorage.setItem(sharedStorageKey, JSON.stringify({ ...current, ...patch, updatedAt: Date.now() }));
    } catch (error) {}
  };

  const readLocation = () => {
    const value = readStorage(locationStorageKey);
    return normalizeZip(value.zip).length === 5 ? value : null;
  };

  const writeLocation = (location) => {
    try {
      window.localStorage.setItem(locationStorageKey, JSON.stringify(location));
    } catch (error) {}
    writeSharedProfile({
      zip: location.zip,
      city: location.city,
      state: location.stateCode,
      latitude: location.latitude,
      longitude: location.longitude
    });
  };

  const formatLocation = (location) => {
    return [location?.city, location?.stateCode || location?.state].filter(Boolean).join(', ');
  };

  const geocodeZip = async (zip) => {
    const postalCode = normalizeZip(zip);
    if (postalCode.length !== 5) throw new Error('Enter a valid 5-digit ZIP code.');

    const url = new URL('https://geocoding-api.open-meteo.com/v1/search');
    Object.entries({ name: postalCode, count: '1', language: 'en', format: 'json', countryCode: 'US' })
      .forEach(([key, value]) => url.searchParams.set(key, value));

    const response = await fetch(url, { credentials: 'omit' });
    if (!response.ok) throw new Error('We could not look up that ZIP code.');

    const data = await response.json();
    const place = data.results?.[0];
    const stateMatch = findState(place?.admin1);
    if (!place || !stateMatch) throw new Error('Enter a valid U.S. ZIP code.');

    return {
      zip: postalCode,
      latitude: place.latitude,
      longitude: place.longitude,
      city: place.name || '',
      state: stateMatch[0],
      stateCode: stateMatch[1]
    };
  };

  const detectLocation = async () => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 4000);

    try {
      const response = await fetch('https://api.bigdatacloud.net/data/reverse-geocode-client?localityLanguage=en', {
        credentials: 'omit',
        signal: controller.signal
      });
      if (!response.ok) return null;

      const data = await response.json();
      if (String(data?.countryCode || '').toUpperCase() !== 'US') return null;
      const zip = normalizeZip(data?.postcode);
      if (zip.length !== 5) return null;
      return await geocodeZip(zip);
    } catch (error) {
      return null;
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const regionForState = (regions, stateCode) => {
    const code = String(stateCode || '').toUpperCase();
    if (code === 'FL') return 'Florida';
    const match = regions.find((region) => (region.stateCodes || []).includes(code));
    return match?.name || '';
  };

  const resolveProgram = (programs, grass, region) => {
    const grassKey = normalize(grass);
    const regionKey = normalize(region);
    const cool = new Set(['kentucky bluegrass', 'perennial ryegrass', 'fine fescue', 'tall fescue', 'northern cool-season']);
    const warm = new Set(['bermuda', 'zoysia', 'st augustine', 'centipede', 'warm season lawn']);

    const matchesGrass = (name) => {
      const value = normalize(name);
      return value === grassKey
        || (cool.has(grassKey) && value.includes('cool season'))
        || (warm.has(grassKey) && value.includes('warm season'));
    };

    const candidates = (programs || []).filter((program) => (program.grassTypes || []).some(matchesGrass));
    if (!candidates.length) return null;

    const regionMatch = candidates.find((program) => {
      const value = normalize(program.region);
      if (regionKey === 'florida' || regionKey === 'southern') {
        return value.includes('florida') || value === 'southern';
      }
      return value === regionKey;
    });

    return regionMatch || candidates[0] || null;
  };

  const stepForNow = (program) => {
    const steps = (program?.steps || []).filter((step) => step?.product);
    if (!steps.length) return null;

    const current = new Date().getMonth() + 1;
    const active = steps.find((step) => (step.activeMonths || []).some((month) => monthNumber(month) === current));
    if (active) return { step: active, timing: 'now', month: current };

    const exactMonth = steps.find((step) => monthNumber(step.month) === current);
    if (exactMonth) return { step: exactMonth, timing: 'now', month: current };

    const future = steps
      .map((step) => ({ step, month: monthNumber(step.month) }))
      .filter((item) => item.month > current)
      .sort((a, b) => a.month - b.month)[0];

    if (future) return { step: future.step, timing: 'next', month: future.month };

    const first = steps
      .map((step) => ({ step, month: monthNumber(step.month) || 13 }))
      .sort((a, b) => a.month - b.month)[0];

    return first ? { step: first.step, timing: 'next', month: first.month <= 12 ? first.month : 0 } : null;
  };

  const seedForGrass = (items, grass) => {
    const grassKey = normalize(grass);
    const direct = (items || []).find((item) => normalize(item.grassName) === grassKey && item.product);
    if (direct) return direct;

    const cool = new Set(['kentucky bluegrass', 'perennial ryegrass', 'fine fescue', 'tall fescue', 'northern cool-season']);
    const warm = new Set(['bermuda', 'zoysia', 'st augustine', 'centipede', 'warm season lawn']);

    return (items || []).find((item) => {
      if (!item.product) return false;
      const key = normalize(item.grassName);
      return (cool.has(grassKey) && key === 'cool season') || (warm.has(grassKey) && key === 'warm season');
    }) || null;
  };

  const isSeedActive = (item) => {
    if (!item) return false;
    const configured = String(item.activeMonths || '').split(',').map((value) => monthNumber(value)).filter(Boolean);
    if (!configured.length) return true;
    const current = new Date().getMonth() + 1;
    return configured.includes(current);
  };

  const recommendationCard = ({ product, kicker, description, buttonLabel, benefits = [] }) => {
    if (!product) return '';
    const image = product.image
      ? '<a class="ls-home-lawn-finder__product-media" href="' + escapeHtml(product.url || '#') + '"><img src="' + escapeHtml(product.image) + '" alt="" loading="lazy" width="320" height="320"></a>'
      : '<div class="ls-home-lawn-finder__product-media"></div>';
    const benefitMarkup = benefits.filter((benefit) => benefit?.title).slice(0, 3)
      .map((benefit) => '<li>' + escapeHtml(benefit.title) + '</li>').join('');

    return '<article class="ls-home-lawn-finder__recommendation">'
      + image
      + '<div class="ls-home-lawn-finder__product-content">'
      + '<p class="ls-home-lawn-finder__kicker">' + escapeHtml(kicker) + '</p>'
      + '<h3 class="ls-home-lawn-finder__product-title">' + escapeHtml(product.title || '') + '</h3>'
      + (description ? '<p class="ls-home-lawn-finder__product-copy">' + escapeHtml(description) + '</p>' : '')
      + (benefitMarkup ? '<ul class="ls-home-lawn-finder__benefits">' + benefitMarkup + '</ul>' : '')
      + '<a class="ls-home-lawn-finder__button" href="' + escapeHtml(product.url || '#') + '">' + escapeHtml(buttonLabel) + '</a>'
      + '</div></article>';
  };

  const initialize = (root) => {
    if (!root || root.dataset.homeLawnFinderInitialized === 'true') return;
    root.dataset.homeLawnFinderInitialized = 'true';

    const regionData = parseJson(root.querySelector('[data-fit-regions-data]')?.textContent, { regions: [] });
    const fertilizerData = parseJson(root.querySelector('[data-fit-fertilizer-data]')?.textContent, { programs: [] });
    const seedData = parseJson(root.querySelector('[data-home-seed-data]')?.textContent, { items: [] });
    const regions = regionData.regions || [];
    const programs = fertilizerData.programs || [];
    const seeds = seedData.items || [];

    const grassStep = root.querySelector('[data-home-step="grass"]');
    const locationStep = root.querySelector('[data-home-step="location"]');
    const resultStep = root.querySelector('[data-home-step="result"]');
    const grassButtons = Array.from(root.querySelectorAll('[data-home-grass]'));
    const grassGrid = root.querySelector('.ls-home-lawn-finder__grass-grid');
    const grassPrevious = root.querySelector('[data-home-grass-prev]');
    const grassNext = root.querySelector('[data-home-grass-next]');
    const locationStatus = root.querySelector('[data-home-location-status]');
    const zipRow = root.querySelector('[data-home-zip-row]');
    const zipInput = root.querySelector('[data-home-zip]');
    const zipSubmit = root.querySelector('[data-home-zip-submit]');
    const resultCards = root.querySelector('[data-home-result-cards]');
    const resultTitle = root.querySelector('[data-home-result-title]');
    const summaryGrass = root.querySelector('[data-home-summary-grass]');
    const summaryLocation = root.querySelector('[data-home-summary-location]');
    const changeGrass = root.querySelector('[data-home-change-grass]');
    const changeLocation = root.querySelector('[data-home-change-location]');
    const programCta = root.querySelector('[data-home-program]');

    let selectedGrass = '';
    let activeLocation = readLocation();
    let lookupStarted = false;

    const updateGrassArrows = () => {
      if (!grassGrid || !grassPrevious || !grassNext) return;
      const maxScroll = grassGrid.scrollWidth - grassGrid.clientWidth;
      grassPrevious.disabled = grassGrid.scrollLeft <= 4;
      grassNext.disabled = maxScroll <= 4 || grassGrid.scrollLeft >= maxScroll - 4;
    };

    const scrollGrass = (direction) => {
      if (!grassGrid) return;
      const card = grassGrid.querySelector('[data-home-grass]');
      const styles = window.getComputedStyle(grassGrid);
      const gap = parseFloat(styles.columnGap || styles.gap || 0);
      const amount = (card?.getBoundingClientRect().width || grassGrid.clientWidth * .8) + gap;
      grassGrid.scrollBy({ left: direction * amount, behavior: 'smooth' });
    };

    const showOnly = (step) => {
      [grassStep, locationStep, resultStep].forEach((item) => {
        if (item) item.hidden = item !== step;
      });
    };

    const selectGrassButton = (name) => {
      const key = normalize(name);
      grassButtons.forEach((button) => {
        const selected = normalize(button.dataset.homeGrass) === key;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    };

    const revealZip = (message = 'Enter your ZIP code so we can match the right regional program.') => {
      if (locationStatus) locationStatus.textContent = message;
      if (zipRow) zipRow.hidden = false;
      if (zipInput) {
        zipInput.value = activeLocation?.zip || readStorage(sharedStorageKey).zip || '';
        zipInput.focus({ preventScroll: true });
      }
      if (zipSubmit) zipSubmit.disabled = normalizeZip(zipInput?.value).length !== 5;
    };

    const currentLocationRegion = () => regionForState(regions, activeLocation?.stateCode || activeLocation?.state);

    const renderResults = () => {
      const region = currentLocationRegion();
      const program = resolveProgram(programs, selectedGrass, region);
      const fertilizer = stepForNow(program);
      const seed = seedForGrass(seeds, selectedGrass);
      const seedActive = root.dataset.showSeed === 'true' && isSeedActive(seed);
      const cards = [];

      if (fertilizer?.step?.product) {
        const timingLabel = fertilizer.timing === 'now'
          ? (root.dataset.fertilizerNowLabel || 'APPLY NOW')
          : (root.dataset.fertilizerNextLabel || 'YOUR NEXT APPLICATION');
        const month = fertilizer.month ? monthName(fertilizer.month) : '';
        const kicker = fertilizer.timing === 'now' ? timingLabel : timingLabel + (month ? ' · ' + month : '');
        const description = fertilizer.step.product.shortDescription || fertilizer.step.description || '';
        cards.push(recommendationCard({
          product: fertilizer.step.product,
          kicker,
          description,
          benefits: fertilizer.step.product.benefits || [],
          buttonLabel: root.dataset.fertilizerButtonLabel || 'Shop Fertilizer'
        }));
      }

      if (seedActive && seed?.product) {
        cards.push(recommendationCard({
          product: seed.product,
          kicker: seed.kicker || root.dataset.seedKicker || 'SEEDING SEASON',
          description: seed.description || seed.product.shortDescription || '',
          buttonLabel: root.dataset.seedButtonLabel || 'Shop Grass Seed'
        }));
      }

      if (!cards.length) {
        cards.push('<div class="ls-home-lawn-finder__recommendation"><div class="ls-home-lawn-finder__product-content" style="grid-column:1/-1;padding:28px;">'
          + '<p class="ls-home-lawn-finder__kicker">' + escapeHtml(root.dataset.emptyKicker || 'YOUR LAWN MATCH') + '</p>'
          + '<h3 class="ls-home-lawn-finder__product-title">' + escapeHtml(root.dataset.emptyTitle || 'Shop products for your lawn') + '</h3>'
          + '<p class="ls-home-lawn-finder__product-copy">' + escapeHtml(root.dataset.emptyCopy || 'We could not find a current application in the lawn program, but you can still shop by category.') + '</p>'
          + '<a class="ls-home-lawn-finder__button" href="' + escapeHtml(root.dataset.fertilizerCollectionUrl || '/collections/lawn-fertilizer') + '">' + escapeHtml(root.dataset.fertilizerButtonLabel || 'Shop Fertilizer') + '</a>'
          + '</div></div>');
      }

      if (resultCards) {
        resultCards.innerHTML = cards.join('');
        resultCards.classList.toggle('is-single', cards.length === 1);
      }

      if (resultTitle) resultTitle.textContent = root.dataset.resultTitle || 'Here’s What Your Lawn Needs';
      if (summaryGrass) summaryGrass.textContent = selectedGrass;
      if (summaryLocation) summaryLocation.textContent = formatLocation(activeLocation);
      if (programCta) programCta.hidden = !program;
      showOnly(resultStep);
    };

    const useLocation = (location) => {
      const stateMatch = findState(location?.stateCode || location?.state);
      const zip = normalizeZip(location?.zip);
      if (!stateMatch || zip.length !== 5) return false;

      activeLocation = {
        zip,
        latitude: location.latitude,
        longitude: location.longitude,
        city: location.city || '',
        state: stateMatch[0],
        stateCode: stateMatch[1]
      };
      writeLocation(activeLocation);
      renderResults();
      return true;
    };

    const requestLocation = async () => {
      showOnly(locationStep);
      if (zipRow) zipRow.hidden = true;

      if (activeLocation && useLocation(activeLocation)) return;
      if (lookupStarted) {
        revealZip();
        return;
      }

      lookupStarted = true;
      if (locationStatus) locationStatus.textContent = 'Finding your location…';
      const detected = await detectLocation();
      if (detected && useLocation(detected)) return;
      revealZip();
    };

    grassButtons.forEach((button) => {
      button.addEventListener('click', () => {
        selectedGrass = button.dataset.homeGrass || '';
        if (!selectedGrass) return;
        selectGrassButton(selectedGrass);
        writeSharedProfile({ grass: selectedGrass });
        requestLocation();
      });
    });

    grassPrevious?.addEventListener('click', () => scrollGrass(-1));
    grassNext?.addEventListener('click', () => scrollGrass(1));
    grassGrid?.addEventListener('scroll', updateGrassArrows, { passive: true });
    window.addEventListener('resize', updateGrassArrows);
    updateGrassArrows();

    if (zipInput && zipSubmit) {
      zipInput.addEventListener('input', () => {
        zipInput.value = normalizeZip(zipInput.value);
        zipSubmit.disabled = zipInput.value.length !== 5;
      });

      zipSubmit.addEventListener('click', async () => {
        const zip = normalizeZip(zipInput.value);
        if (zip.length !== 5) return;
        zipSubmit.disabled = true;
        zipSubmit.textContent = root.dataset.zipCheckingLabel || 'Checking…';

        try {
          const location = await geocodeZip(zip);
          useLocation(location);
        } catch (error) {
          if (locationStatus) locationStatus.textContent = error.message;
          zipSubmit.disabled = false;
          zipSubmit.textContent = root.dataset.zipButtonLabel || 'Show My Products';
        }
      });
    }

    if (changeGrass) {
      changeGrass.addEventListener('click', () => {
        showOnly(grassStep);
      });
    }

    if (changeLocation) {
      changeLocation.addEventListener('click', () => {
        activeLocation = null;
        showOnly(locationStep);
        revealZip('Enter a different ZIP code.');
      });
    }

    const savedProfile = readStorage(sharedStorageKey);
    const savedGrass = savedProfile.grass || '';
    const savedGrassButton = grassButtons.find((button) => normalize(button.dataset.homeGrass) === normalize(savedGrass));

    if (savedGrassButton) {
      selectedGrass = savedGrassButton.dataset.homeGrass || '';
      selectGrassButton(selectedGrass);
      if (activeLocation) {
        renderResults();
      }
    }
  };

  const boot = () => document.querySelectorAll('[data-ls-home-lawn-finder]').forEach(initialize);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-ls-home-lawn-finder]').forEach(initialize);
  });
})();
